import React from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { Global } from '@emotion/react';

import { EVENT_TYPE, type AnalyticsEventPayload } from '@atlaskit/editor-common/analytics';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { PMPlugin, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import { isEmptyDocument } from '@atlaskit/editor-common/utils';
import { WithPluginState } from '@atlaskit/editor-common/with-plugin-state';
import type { EditorState, PluginKey } from '@atlaskit/editor-prosemirror/state';
import { type EditorView } from '@atlaskit/editor-prosemirror/view';
import { isResolvingMentionProvider, type MentionProvider } from '@atlaskit/mention/resource';
import { isPromise } from '@atlaskit/mention/types';
import { useAIThemeColor } from '@atlassian/generative-ai-modal/styles/theme';

import type { AIProactivePlugin } from './aiProactivePluginType';
import { createProactiveAIPlugin } from './pm-plugins/ai-proactive/ai-proactive-plugin';
import { aiProactivePluginKey } from './pm-plugins/ai-proactive/ai-proactive-plugin-key';
import { ProactiveDecorations } from './pm-plugins/ai-proactive/decorations';
import type { AIProactivePluginState } from './pm-plugins/ai-proactive/states';
import {
	getBlockFromRecommendationId,
	getRecommendationByFilters,
	getUnreadRecommendationsCount,
} from './pm-plugins/ai-proactive/utils';
import { deriveProactiveAIApiUrl } from './pm-plugins/ai-proactive/utils/url';
import { createGetEditorViewPlugin } from './pm-plugins/get-editor-view/get-editor-view-plugin';
import type { AIProactivePluginSharedState } from './types';
import { getContextPanel } from './ui/components/context-panel/context-panel';
import { ModalRegion } from './ui/components/ModalRegion/ModalRegion';
import { globalStyles } from './ui/components/ModalRegion/styles';
import { ModalRegionErrorBoundary } from './ui/components/ModalRegionErrorBoundary/ModalRegionErrorBoundary';
import { ProactivePreviewScreenWithLogic } from './ui/components/ProactivePreviewScreenWithLogic/ProactivePreviewScreenWithLogic';
import { getAIPrimaryToolbarComponent } from './ui/toolbars/primary-toolbar';

const GlobalWrapper = ({ children }: { children: React.ReactNode }) => {
	const aiThemeColor = useAIThemeColor();

	return (
		<>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */}
			<Global styles={globalStyles(aiThemeColor)} />
			{children}
		</>
	);
};

export const aiProactivePlugin: AIProactivePlugin = ({ api, config }) => {
	let editorView: EditorView | undefined;
	const setEditorView = (newEditorView: EditorView) => {
		editorView = newEditorView;
	};

	let providerFactory: ProviderFactory;
	const setProviderFactory = (newProviderFactory: ProviderFactory) => {
		providerFactory = newProviderFactory;
	};

	let mentionProvider: MentionProvider | undefined;

	const setMentionProvider = () => {
		const handler = (_name: string, providerPromise?: Promise<MentionProvider>) => {
			if (providerPromise) {
				providerPromise.then((provider) => {
					mentionProvider = provider;
					providerFactory.unsubscribe('mentionProvider', handler);
				});
			}
		};
		providerFactory.subscribe('mentionProvider', handler);
	};

	/**
	 * Helper function to retrieve the name details for a mention from the mentionProvider
	 */
	const getMentionNameDetails = async (id: string) => {
		if (!mentionProvider || !isResolvingMentionProvider(mentionProvider)) {
			return;
		}

		const nameDetails = mentionProvider.resolveMentionName(id);

		if (isPromise(nameDetails)) {
			const mention = await nameDetails;
			return mention;
		}

		return nameDetails;
	};

	const { product, aiGlobalOptIn } = config;

	const aiOptIn = aiGlobalOptIn || {
		status: 'enabled',
		triggerOptInFlow: () => {},
	};

	api?.primaryToolbar?.actions.registerComponent({
		name: 'aiExperience',
		component: getAIPrimaryToolbarComponent({ api, aiGlobalOptIn: aiOptIn }),
	});

	return {
		name: 'aiProactive',
		pmPlugins: () => {
			const plugins: PMPlugin[] = [
				{
					name: 'ai-proactive-get-editor-view-and-provider-factory',
					plugin: (options: PMPluginFactoryParams) => {
						setProviderFactory(options.providerFactory);
						setMentionProvider();
						return createGetEditorViewPlugin({
							setEditorView: setEditorView,
						});
					},
				},
				{
					name: 'ai-proactive-plugin',
					plugin: ({ dispatch, getIntl, dispatchAnalyticsEvent }: PMPluginFactoryParams) => {
						// FIXME fix the analytics event payload
						const payload = {
							action: 'init',
							actionSubject: 'editor',
							actionSubjectId: 'editorPluginAIProactive',
							eventType: EVENT_TYPE.TRACK,
							attributes: {
								aiGlobalOptIn: aiOptIn?.status,
							},
						} as unknown as AnalyticsEventPayload;
						dispatchAnalyticsEvent(payload);

						return createProactiveAIPlugin({
							dispatch,
							getIntl,
							proactiveAIConfig: {
								enabled: true,
								defaultToggledState: false,
								apiUrl: deriveProactiveAIApiUrl(),
								timings: {
									// disabling the current chunk checking
									currentChunks: -1,
									currentChunksMaxWait: 30000,
									nonCurrentChunks: 5000,
								},
								documentChecker: {
									enabled: true,
									blocksPerRequest: 7,
									timings: {
										delayBetweenChecks: 0,
										numberOfRequestsForIncrease: 2,
										delayIncrease: 0,
										maxDelay: 0,
									},
								},
							},
							product: product,
							getMentionNameDetails,
							api,
						});
					},
				},
			];

			return plugins;
		},

		getSharedState(state: EditorState | undefined): AIProactivePluginSharedState {
			if (!state) {
				return {
					isProactiveEnabled: false,
					recommendations: [],
					displayAllSuggestions: false,
					isLoading: false,
					isEmptyDocument: false,
					hasNoMoreSuggestions: true,
					displayAllPanelSuggestions: false,
					isProactiveContextPanelOpen: false,
					showNewRecommendationsNotification: false,
					unreadRecommendationCount: 0,
				};
			}

			const proactivePluginState = aiProactivePluginKey.getState(state) ?? {};
			const filteredRecommendations = getRecommendationByFilters(proactivePluginState) ?? [];

			return {
				isProactiveEnabled: proactivePluginState.isProactiveEnabled ?? false,
				recommendations: filteredRecommendations,
				selectedRecommendationId: proactivePluginState.selectedRecommendationId,
				hoveredRecommendationId: proactivePluginState.hoveredRecommendationId,
				isLoading: proactivePluginState.isLoading,
				isEmptyDocument: isEmptyDocument(state.doc),
				displayAllSuggestions: proactivePluginState?.settings?.displayAllSuggestions ?? false,
				displayAllPanelSuggestions:
					proactivePluginState?.settings?.displayAllPanelSuggestions ?? false,
				hasNoMoreSuggestions: proactivePluginState.hasNoMoreSuggestions,
				isProactiveContextPanelOpen: proactivePluginState.isProactiveContextPanelOpen ?? false,
				showNewRecommendationsNotification:
					proactivePluginState.showNewRecommendationsNotification ?? false,
				unreadRecommendationCount: getUnreadRecommendationsCount(proactivePluginState),
			};
		},

		contentComponent: ({ editorView, wrapperElement, appearance, providerFactory }) => {
			const endExperience = () => {
				editorView.focus();
			};

			const renderProactiveModalRegion = (aiProactivePluginState: AIProactivePluginState) => {
				// Prioritise getting position from inlineDecoration,
				// otherwise get decoration position from start or end widget

				const decoration = aiProactivePluginState.decorationSet
					.find(
						undefined,
						undefined,
						(spec) => spec.key === ProactiveDecorations.RECOMMENDATION_SELECTED,
					)
					.shift();

				if (!decoration) {
					return null;
				}
				const modalDecorationElements = editorView?.dom.querySelectorAll<HTMLElement>(
					'.ai-proactive-recommendation-selected',
				);

				// If there's more then 1 selected node, which can happen when the cursor is in the middle of a paragrpah
				// the decorations are split around the cursor. In this situation we should just use the decorations first
				// parent.
				const modalDecorationElement: HTMLElement =
					modalDecorationElements.length > 1
						? // Ignored via go/ees005
							// eslint-disable-next-line @atlaskit/editor/no-as-casting
							(modalDecorationElements?.[modalDecorationElements.length - 1]
								?.offsetParent as HTMLElement)
						: modalDecorationElements?.[0];

				// This protects against cases where there is an active decoration
				// but the document has not been updated to include it
				if (!modalDecorationElement) {
					return null;
				}

				return (
					<ModalRegion
						key={aiProactivePluginState.selectedRecommendationId}
						decoration={decoration}
						modalDecorationElement={modalDecorationElement}
						editorView={editorView}
						editorRelativeWrapper={wrapperElement?.offsetParent}
						endExperience={endExperience}
						appearance={appearance}
						decorationSet={aiProactivePluginState.decorationSet}
						autoScroll
					>
						<ProactivePreviewScreenWithLogic
							api={api}
							config={config}
							aiProactivePluginState={aiProactivePluginState}
							editorView={editorView}
							providerFactory={providerFactory}
						/>
					</ModalRegion>
				);
			};

			const pluginsToTrack: Record<string, PluginKey> = {
				aiProactivePluginState: aiProactivePluginKey,
			};

			return (
				<GlobalWrapper>
					<WithPluginState
						plugins={pluginsToTrack}
						render={({ aiProactivePluginState }) => {
							// The WithPluginState function does not support typing the plugin states it returns
							const { selectedRecommendationId } = aiProactivePluginState;
							const { recommendation } = getBlockFromRecommendationId(
								aiProactivePluginState,
								selectedRecommendationId,
							);

							// if there is a matching selectedRecommendationId, we should trigger aiProactive Preview
							if (!!recommendation) {
								const proactiveModalRegion = renderProactiveModalRegion(aiProactivePluginState);
								// TODO POSTEAP
								// This error boundary will be unexpectedly positioned
								// as it's not wrapped in the ModalRegion.
								// In the case the modal region throws an exception
								// we should come up with an alternative positioning
								// strategy for error feedback.
								return (
									<ModalRegionErrorBoundary api={api} onCloseFallback={endExperience}>
										{proactiveModalRegion}
									</ModalRegionErrorBoundary>
								);
							}

							return null;
						}}
					/>
				</GlobalWrapper>
			);
			// return (
			// 	<ContentComponent
			// 		api={api}
			// 		editorView={editorView}
			// 		wrapperElement={wrapperElement}
			// 		appearance={appearance}
			// 		product={config.product}
			// 		providerFactory={providerFactory}
			// 	/>
			// );
		},

		pluginsOptions: {
			contextPanel: getContextPanel(config, () => editorView)(api),
		},
	};
};
